import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { supabase } from './supabaseClient';

const SessionContext = createContext();

async function buildWingitSession(authSession) {
  if (!authSession?.user?.id) {
    return authSession || null;
  }

  const { data: profile, error } = await supabase
    .from('users')
    .select(
      'id, auth_id, email, full_name, role, organisation_id, organisation_code, org_name, home_airfield, membership_tier, membership_billing_cycle, is_active'
    )
    .eq('auth_id', authSession.user.id)
    .maybeSingle();

  return {
    ...authSession,
    profile: error ? null : profile || null,
    profileError: error || null,
  };
}

export function SessionProvider({ children }) {
  // undefined = restoring session
  // null = logged out
  const [session, setSession] = useState(undefined);

  useEffect(() => {
    let isMounted = true;

    async function applySession(authSession) {
      if (!authSession) {
        if (isMounted) {
          setSession(null);
        }
        return;
      }

      try {
        const wingitSession = await buildWingitSession(authSession);

        if (isMounted) {
          setSession(wingitSession);
        }
      } catch (error) {
        console.error('Failed to load Wingit profile:', error);

        if (isMounted) {
          setSession({
            ...authSession,
            profile: null,
            profileError: error,
          });
        }
      }
    }

    async function loadInitialSession() {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        await applySession(data?.session ?? null);
      } catch (error) {
        console.error('Failed to restore session:', error);

        if (isMounted) {
          setSession(null);
        }
      }
    }

    loadInitialSession();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, authSession) => {
        if (event === 'PASSWORD_RECOVERY') {
          const targetPath = '/reset-password';
          const currentPath = window.location.pathname;

          if (currentPath !== targetPath) {
            window.location.replace(
              `${targetPath}${window.location.search}${window.location.hash}`
            );
            return;
          }
        }

        // Run profile loading after the auth callback has finished.
        window.setTimeout(() => {
          applySession(authSession);
        }, 0);
      }
    );

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}